import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PageHeader, Panel, PanelGrid, Button, Segmented, Switch, Field, TextInput, ComboBox, Dialog, Banner, Spinner,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { BarcodeCameraPanelNext, ScanExamineConfirmDialogNext, ScanGradeDialogNext } from '../../components/ScanNext.jsx';
import { MultiCheck, Row } from '../../next/ui';

/**
 * מדבקות וברקודים — new design. Same endpoints and flow as LabelsPage: scan (hardware / typing / camera) in
 * lookup / mark-examined (auto, auto-register) / grade modes, sticker layout, PDF exports (by kolel, by exam codes
 * with period choice, by location, by location + kolel code) and printing stickers with filters.
 */
const TRACTATE_NAMES = [
  'ברכות', 'שבת', 'עירובין', 'פסחים', 'יומא', 'סוכה', 'ביצה', 'ראש השנה', 'תענית',
  'מגילה', 'מועד קטן', 'חגיגה', 'יבמות', 'כתובות', 'נדרים', 'נזיר', 'סוטה', 'גיטין',
  'קידושין', 'בבא קמא', 'בבא מציעא', 'בבא בתרא', 'סנהדרין', 'מכות', 'שבועות',
  'עבודה זרה', 'הוריות', 'זבחים', 'מנחות', 'חולין', 'בכורות', 'ערכין', 'תמורה',
  'כריתות', 'מעילה', 'תמיד', 'נידה',
].map((t) => ({ value: t, label: t }));
const AUTO_EXAMINE_KEY = 'labels.scan.autoExamine';
const AUTO_REGISTER_KEY = 'labels.scan.autoRegister';
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const fullName = (m) => `${m?.firstName || ''} ${m?.lastName || ''}`.trim();

async function downloadBlob(path, body, filename, type) {
  const { data } = await api.post(path, body || {}, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
async function parseExportError(err, fallback) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).error || fallback; } catch { return fallback; }
  }
  return data?.error || fallback;
}
function defaultExportPeriodIds(periods) {
  const sorted = [...periods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const active = sorted.find((p) => p.isActive) || sorted[sorted.length - 1] || null;
  if (!active) return [];
  const prev = sorted.filter((p) => (p.order ?? 0) < (active.order ?? 0)).at(-1);
  return [prev, active].filter(Boolean).map((p) => String(p._id));
}
function buildCandidates(lookup) {
  if (!lookup) return [];
  if (lookup.kind === 'exam') {
    if (!lookup.registrationId) return [];
    return [{
      registrationId: lookup.registrationId,
      exam: lookup.exam || null,
      periodId: lookup.period?._id || lookup.registration?.periodId || lookup.exam?.periodId || null,
      period: lookup.period || null,
      examined: !!lookup.examined,
      gemaraSelection: lookup.gemaraSelection || lookup.registration?.gemaraSelection || [],
    }];
  }
  return (lookup.registrations || []).filter((r) => r?._id).map((r) => ({
    registrationId: r._id,
    exam: r.exam || null,
    periodId: r.periodId || r.exam?.periodId || null,
    period: r.period || null,
    examined: !!r.examined,
    gemaraSelection: r.gemaraSelection || [],
  }));
}
const isFullyExamined = (c) => (!c ? false : (c.gemaraSelection || []).length ? c.gemaraSelection.every((s) => s.examined) : !!c.examined);
const readFlag = (key) => { try { return localStorage.getItem(key) === '1'; } catch { return false; } };
const writeFlag = (key, on) => { try { localStorage.setItem(key, on ? '1' : '0'); } catch { /* ignore */ } };

export default function LabelsPageNext() {
  const { hasPermission } = useAuth();
  const canExamine = hasPermission('examRegistration', 'create');
  const canGrade = hasPermission('grades', 'create');

  const [scanMode, setScanMode] = useState('lookup');
  const [autoExamine, setAutoExamine] = useState(() => readFlag(AUTO_EXAMINE_KEY));
  const [autoRegister, setAutoRegister] = useState(() => !IS_OPEN_GEMARA && readFlag(AUTO_REGISTER_KEY));
  const [barcodeInput, setBarcodeInput] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [examine, setExamine] = useState({ open: false, member: null, candidates: [] });
  const [examineSaving, setExamineSaving] = useState(false);
  const [grade, setGrade] = useState({ open: false, member: null, candidates: [] });

  const [tractateFilter, setTractateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');
  const [exporting, setExporting] = useState('');
  const [exportError, setExportError] = useState('');
  const [barcodeSvg, setBarcodeSvg] = useState('');
  const [periods, setPeriods] = useState([]);
  const [examExportOpen, setExamExportOpen] = useState(false);
  const [exportPeriodIds, setExportPeriodIds] = useState([]);
  const [stickerLayout, setStickerLayout] = useState(24);
  const barcodeRef = useRef(null);

  const modes = useMemo(() => ['lookup', ...(canExamine ? ['examine'] : []), ...(canGrade ? ['grade'] : [])], [canExamine, canGrade]);
  useEffect(() => { if (!modes.includes(scanMode)) setScanMode('lookup'); }, [modes, scanMode]);
  useEffect(() => { writeFlag(AUTO_EXAMINE_KEY, autoExamine); }, [autoExamine]);
  useEffect(() => { if (!IS_OPEN_GEMARA) writeFlag(AUTO_REGISTER_KEY, autoRegister); }, [autoRegister]);
  useEffect(() => {
    let cancelled = false;
    api.get('/exam-periods').then(({ data }) => {
      if (!cancelled) setPeriods(Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []));
    }).catch(() => { if (!cancelled) setPeriods([]); });
    return () => { cancelled = true; };
  }, []);

  const focusBarcode = useCallback(() => { setTimeout(() => barcodeRef.current?.focus?.(), 50); }, []);
  const resetAfterAction = useCallback((message) => {
    setBarcodeInput('');
    setLookupResult(null);
    setLookupError('');
    setActionSuccess(message || '');
    focusBarcode();
  }, [focusBarcode]);

  useEffect(() => {
    let cancelled = false;
    const code = lookupResult?.barcode;
    if (!code) { setBarcodeSvg(''); return undefined; }
    api.get(`/labels/barcode/${encodeURIComponent(code)}`, { responseType: 'text', headers: { Accept: 'image/svg+xml' } })
      .then(({ data }) => { if (!cancelled) setBarcodeSvg(data); })
      .catch(() => { if (!cancelled) setBarcodeSvg(''); });
    return () => { cancelled = true; };
  }, [lookupResult?.barcode]);

  const markExamined = useCallback(async ({ registrationId, examined = true, gemaraExamined, gemaraSelection }) => {
    let body;
    if (gemaraSelection) body = { gemaraSelection };
    else if (gemaraExamined) body = { gemaraExamined };
    else body = { examined: !!examined };
    await api.put(`/registrations/${registrationId}`, body);
  }, []);

  const examineFromLookup = useCallback(async (data) => {
    const candidates = buildCandidates(data);
    if (!candidates.length) { setLookupError('לא נמצאה הרשמה לסימון כנבחן'); setLookupResult(data); return; }
    const needsChoice = candidates.length > 1 || candidates.some((c) => (c.gemaraSelection || []).length > 1);
    if (!autoExamine || needsChoice) { setExamine({ open: true, member: data.member, candidates }); setLookupResult(data); return; }
    const c = candidates[0];
    if (isFullyExamined(c)) { resetAfterAction(`${fullName(data.member)} כבר מסומן כנבחן`); return; }
    try {
      setLookupLoading(true);
      const gemara = c.gemaraSelection || [];
      if (gemara.length) await markExamined({ registrationId: c.registrationId, gemaraExamined: gemara.map((sel) => ({ tractate: sel.tractate, examined: true })) });
      else await markExamined({ registrationId: c.registrationId, examined: true });
      resetAfterAction(`${fullName(data.member) || 'החבר'} סומן כנבחן`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
      setLookupResult(data);
    } finally {
      setLookupLoading(false);
    }
  }, [autoExamine, markExamined, resetAfterAction]);

  const gradeFromLookup = useCallback((data) => {
    const candidates = buildCandidates(data);
    setLookupResult(data);
    if (!candidates.length) { setLookupError('לא נמצאה הרשמה להזנת ציון'); return; }
    setGrade({ open: true, member: data.member, candidates });
  }, []);

  const scanExamineRegister = useCallback(async (code) => {
    try {
      setLookupLoading(true);
      const { data } = await api.post('/labels/scan-examine', { code, registerIfMissing: true });
      const who = fullName(data.member) || 'החבר';
      resetAfterAction(data.created || data.reactivated ? `${who} נרשם וסומן כנבחן` : data.alreadyExamined ? `${who} כבר מסומן כנבחן` : `${who} סומן כנבחן`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
      focusBarcode();
    }
  }, [resetAfterAction, focusBarcode]);

  const runBarcodeAction = useCallback(async (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    setLookupError('');
    setLookupResult(null);
    setActionSuccess('');
    if (scanMode === 'examine' && autoRegister && !IS_OPEN_GEMARA) { await scanExamineRegister(code); return; }
    setLookupLoading(true);
    try {
      const { data } = await api.post('/labels/lookup-barcode', { code });
      if (scanMode === 'examine') await examineFromLookup(data);
      else if (scanMode === 'grade') gradeFromLookup(data);
      else setLookupResult(data);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בחיפוש');
    } finally {
      setLookupLoading(false);
      if (scanMode === 'lookup') focusBarcode();
    }
  }, [scanMode, autoRegister, scanExamineRegister, examineFromLookup, gradeFromLookup, focusBarcode]);

  const onCameraScan = useCallback((code) => { setBarcodeInput(code); runBarcodeAction(code); }, [runBarcodeAction]);

  const confirmExamine = async (payload) => {
    setExamineSaving(true);
    setLookupError('');
    try {
      await markExamined(payload);
      setExamine((x) => ({ ...x, open: false }));
      resetAfterAction(`${fullName(examine.member) || 'החבר'} סומן כנבחן`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
    } finally {
      setExamineSaving(false);
    }
  };

  const printLabels = async () => {
    setPrintLoading(true);
    setPrintError('');
    try {
      const body = { layout: stickerLayout };
      if (tractateFilter) body.tractate = tractateFilter;
      if (cityFilter) body.city = cityFilter;
      const { data } = await api.post('/labels/print', body, { responseType: 'text', headers: { Accept: 'text/html' } });
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(data);
        w.document.close();
        w.onload = () => w.print();
      }
    } catch (err) {
      setPrintError(err.response?.data?.error || 'שגיאה ביצירת מדבקות');
    } finally {
      setPrintLoading(false);
    }
  };
  const runExport = async (key, path, body, filename, type, fallback) => {
    setExporting(key);
    setExportError('');
    try {
      await downloadBlob(path, body, filename, type);
      return true;
    } catch (err) {
      setExportError(await parseExportError(err, fallback));
      return false;
    } finally {
      setExporting('');
    }
  };
  const exportByExam = async () => {
    if (!exportPeriodIds.length) { setExportError('יש לבחור לפחות מועד אחד'); return; }
    if (await runExport('exam', '/labels/export-by-exam', { periodIds: exportPeriodIds, layout: stickerLayout }, 'ברקודים-לפי-מבחן.zip', 'application/zip', 'שגיאה בייצוא לפי מבחנים')) setExamExportOpen(false);
  };

  const actionLabel = scanMode === 'examine' ? 'סמן' : scanMode === 'grade' ? 'הזן ציון' : 'חפש';
  const actionIcon = scanMode === 'examine' ? 'check' : scanMode === 'grade' ? 'task' : 'search';
  const m = lookupResult?.member;
  const cameraPaused = examine.open || grade.open || lookupLoading;

  return (
    <>
      <PageHeader title="מדבקות וברקודים" sub="סריקת ברקוד מבחן (קודאישי-קודמבחן), סימון נבחן, הזנת ציון, ייצוא PDF והדפסת מדבקות" />

      <PanelGrid cols={2}>
        <Panel title="סריקת ברקוד" icon="hash">
          <div className="nx-stack">
            {modes.length > 1 ? (
              <Segmented
                value={scanMode}
                onChange={(v) => { setScanMode(v); setLookupError(''); setActionSuccess(''); focusBarcode(); }}
                items={[{ value: 'lookup', label: 'חיפוש' }, ...(canExamine ? [{ value: 'examine', label: 'סמן נבחן' }] : []), ...(canGrade ? [{ value: 'grade', label: 'הזנת ציון' }] : [])]}
              />
            ) : null}
            {scanMode === 'examine' ? (
              <div className="nx-stack" style={{ gap: 4 }}>
                <Switch checked={autoExamine} disabled={autoRegister && !IS_OPEN_GEMARA} onChange={setAutoExamine} label="סימון אוטומטי (בלי אישור)" />
                {!IS_OPEN_GEMARA ? <Switch checked={autoRegister} onChange={setAutoRegister} label="רישום אוטומטי אם חסר" /> : null}
              </div>
            ) : null}
            <p className="nx-desc">פורמט: קוד אישי-קוד מבחן (למשל 1234-56). ניתן גם לקוד אישי בלבד. סורק חומרה, הקלדה או מצלמה.</p>
            <Row>
              <div style={{ flex: '1 1 160px' }}>
                <TextInput
                  ref={barcodeRef}
                  ltr
                  aria-label="ברקוד / קוד אישי"
                  placeholder="1234-56"
                  autoFocus
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runBarcodeAction(barcodeInput); } }}
                />
              </div>
              <Button variant="primary" icon={actionIcon} disabled={lookupLoading} onClick={() => runBarcodeAction(barcodeInput)}>{lookupLoading ? '…' : actionLabel}</Button>
              <Button variant={cameraActive ? 'primary' : undefined} icon="video" onClick={() => setCameraActive((v) => !v)}>{cameraActive ? 'עצור מצלמה' : 'מצלמה'}</Button>
            </Row>
            {lookupError ? <Banner tone="danger" icon="alert" onClose={() => setLookupError('')}>{lookupError}</Banner> : null}
            {actionSuccess ? <Banner tone="success" icon="check" onClose={() => setActionSuccess('')}>{actionSuccess}</Banner> : null}

            <BarcodeCameraPanelNext active={cameraActive} paused={cameraPaused} onScan={onCameraScan} onStop={() => setCameraActive(false)} />

            {lookupResult && scanMode === 'lookup' && m ? (
              <div className="nx-item" style={{ alignItems: 'flex-start' }}>
                <div className="nx-stack" style={{ flex: 1, gap: 6 }}>
                  <b style={{ fontSize: 16 }}>{fullName(m)}</b>
                  <dl className="nx-kv">
                    {m.fatherName ? <div className="nx-kv__row"><dt>בן</dt><dd>{m.fatherName}</dd></div> : null}
                    <div className="nx-kv__row"><dt>קוד אישי</dt><dd><span className="nx-num">{m.personalCode}</span></dd></div>
                    {m.kolelRef?.name || m.kolel ? <div className="nx-kv__row"><dt>כולל</dt><dd>{m.kolelRef?.name || m.kolel}</dd></div> : null}
                    {m.phone ? <div className="nx-kv__row"><dt>טלפון</dt><dd dir="ltr">{m.phone}</dd></div> : null}
                    {m.city ? <div className="nx-kv__row"><dt>עיר</dt><dd>{m.city}</dd></div> : null}
                  </dl>
                  {lookupResult.kind === 'exam' && lookupResult.exam ? (
                    <>
                      <hr className="nx-hr" />
                      <b>{`מבחן ${lookupResult.exam.code} — ${lookupResult.exam.name}`}</b>
                      {lookupResult.exam.material ? <small className="ck-muted">{lookupResult.exam.material}</small> : null}
                      {lookupResult.period?.name ? <small className="ck-muted">{`זמן: ${lookupResult.period.name}`}</small> : null}
                      {lookupResult.registrationId ? <small className="ck-muted">{`נבחן: ${lookupResult.examined || (lookupResult.gemaraSelection || []).some((s) => s.examined) ? 'כן' : 'לא'}`}</small> : null}
                    </>
                  ) : null}
                  {lookupResult.kind === 'personal' && lookupResult.registrations?.length ? (
                    <>
                      <hr className="nx-hr" />
                      <b>{`הרשמות פעילות (${lookupResult.registrations.length})`}</b>
                      {lookupResult.registrations.map((reg) => (
                        <small key={reg._id} className="ck-muted">
                          {reg.exam
                            ? `${reg.exam.code != null ? `[${reg.exam.code}] ` : ''}${reg.exam.name}${reg.exam.material ? ` — ${reg.exam.material}` : ''}`
                            : (reg.gemaraSelection?.map((sel) => `${sel.tractate} ${sel.ranges?.map((r) => `${r.from}-${r.to}`).join(', ')}`).join(' | ') || 'ללא פרטים')}
                          {reg.examined || (reg.gemaraSelection || []).some((s) => s.examined) ? ' — נבחן' : ''}
                        </small>
                      ))}
                    </>
                  ) : null}
                  {lookupResult.barcode ? (
                    <>
                      <hr className="nx-hr" />
                      <div style={{ textAlign: 'center' }}>
                        <small className="ck-muted nx-num">{lookupResult.barcode}</small>
                        {/* SVG generated by our own /labels/barcode endpoint */}
                        {barcodeSvg ? <div><span className="nx-barcode" dangerouslySetInnerHTML={{ __html: barcodeSvg }} /></div> : null}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </Panel>

        <div className="nx-stack">
          <Panel title="פורמט מדבקות לדף" sub="חל על הדפסה ועל כל ייצואי ה-PDF (TANEX 70מ״מ)" icon="grid">
            <Segmented ariaLabel="פורמט מדבקות לדף" value={stickerLayout} onChange={setStickerLayout} items={[{ value: 21, label: '21 מדבקות' }, { value: 24, label: '24 מדבקות' }, { value: 33, label: '33 מדבקות' }]} />
          </Panel>

          <Panel title="ייצוא ברקודים ל-PDF" sub="כוללים: זמן פעיל + שעבר. מבחנים/מוקדים/מוקד+כולל: קובץ נפרד לכל עיר; בתוך מוקד+כולל דף לכל קוד כולל." icon="file">
            <div className="nx-stack" style={{ gap: 8 }}>
              <Button variant="primary" icon="file" disabled={!!exporting} onClick={() => runExport('kolel', '/labels/export-by-kolel', { layout: stickerLayout }, 'ברקודים-לפי-כולל.pdf', 'application/pdf', 'שגיאה בייצוא לפי כוללים')}>
                {exporting === 'kolel' ? 'מייצא…' : 'ייצוא לפי כוללים (זמן פעיל + שעבר)'}
              </Button>
              <Button icon="file" disabled={!!exporting} onClick={() => { setExportError(''); setExportPeriodIds(defaultExportPeriodIds(periods)); setExamExportOpen(true); }}>ייצוא לפי קודי מבחן (בחירת מועדים)</Button>
              <Button icon="file" disabled={!!exporting} onClick={() => runExport('location', '/labels/export-by-location', { layout: stickerLayout }, 'ברקודים-לפי-מוקד.zip', 'application/zip', 'שגיאה בייצוא לפי מוקדי בחינה')}>
                {exporting === 'location' ? 'מייצא…' : 'ייצוא לפי מוקדי בחינה (קובץ לכל עיר)'}
              </Button>
              <Button icon="file" disabled={!!exporting} onClick={() => runExport('locationKolel', '/labels/export-by-location-kolel', { layout: stickerLayout }, 'ברקודים-לפי-מוקד-וכולל.zip', 'application/zip', 'שגיאה בייצוא לפי מוקד וקוד כולל')}>
                {exporting === 'locationKolel' ? 'מייצא…' : 'ייצוא לפי מוקדים + קוד כולל (קובץ לכל עיר)'}
              </Button>
              {exportError && !examExportOpen ? <Banner tone="danger" icon="alert" onClose={() => setExportError('')}>{exportError}</Banner> : null}
            </div>
          </Panel>

          <Panel title="הדפסת מדבקות" sub="סינון חברים והדפסת מדבקות עם קוד אישי וברקוד" icon="file">
            <div className="nx-stack">
              <Field label="סינון לפי מסכת">
                <ComboBox value={tractateFilter ? { value: tractateFilter, label: tractateFilter } : null} onChange={(o) => setTractateFilter(o?.value || '')} options={TRACTATE_NAMES} getLabel={(o) => o?.label || ''} getKey={(o) => o?.value} placeholder="כל המסכתות" clearable />
              </Field>
              <Field label="סינון לפי עיר"><TextInput value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} /></Field>
              <Button variant="primary" icon="file" disabled={printLoading} onClick={printLabels}>{printLoading ? 'מכין…' : 'הדפס מדבקות'}</Button>
              {printError ? <Banner tone="danger" icon="alert" onClose={() => setPrintError('')}>{printError}</Banner> : null}
            </div>
          </Panel>
        </div>
      </PanelGrid>

      <ScanExamineConfirmDialogNext open={examine.open} onClose={() => setExamine((x) => ({ ...x, open: false }))} member={examine.member} candidates={examine.candidates} onConfirm={confirmExamine} saving={examineSaving} />
      <ScanGradeDialogNext
        open={grade.open}
        onClose={() => { setGrade((x) => ({ ...x, open: false })); focusBarcode(); }}
        member={grade.member}
        candidates={grade.candidates}
        onSaved={() => resetAfterAction(`הציון ל${fullName(grade.member) || 'חבר'} נשמר`)}
      />

      <Dialog
        open={examExportOpen}
        onClose={() => exporting !== 'exam' && setExamExportOpen(false)}
        title="ייצוא ברקודים לפי קוד מבחן"
        icon="file"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" disabled={exporting === 'exam'} onClick={() => setExamExportOpen(false)}>ביטול</Button>
            <Button variant="primary" icon="download" disabled={exporting === 'exam' || !exportPeriodIds.length} onClick={exportByExam}>{exporting === 'exam' ? 'מייצא…' : 'הורד ZIP'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <p className="nx-desc">בחרו מועדים להנפקה. כל מועד וקוד מבחן יופיעו בדף נפרד, וקובץ נפרד לכל עיר.</p>
          <Field label="מועדים">
            <MultiCheck value={exportPeriodIds} onChange={setExportPeriodIds} options={periods.map((p) => ({ value: String(p._id), label: `${p.name}${p.isActive ? ' (פעיל)' : ''}` }))} maxHeight={220} />
          </Field>
          <Row>
            <Button size="sm" variant="ghost" disabled={exporting === 'exam'} onClick={() => setExportPeriodIds(periods.map((p) => String(p._id)))}>הכל</Button>
            <Button size="sm" variant="ghost" disabled={exporting === 'exam'} onClick={() => setExportPeriodIds(defaultExportPeriodIds(periods))}>פעיל + שעבר</Button>
          </Row>
          {exportError ? <Banner tone="danger" icon="alert">{exportError}</Banner> : null}
          {exporting === 'exam' ? <Row><Spinner size={16} /><small className="ck-muted">מייצא…</small></Row> : null}
        </div>
      </Dialog>
    </>
  );
}
